import { salesPointConfigurationSelector } from '@/features/configuration/configurationSelector';
import { findExchangeOffersByToken } from '@/features/journey-change/journeyChangeService';
import { resetChangeTripsSelection } from '@/features/manage/manageActions';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import { fetchBooking } from '@/features/purchase/purchaseService';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { type ApiErrorResponse } from '@/types/api';
import {
  type BookedTripDTO,
  type BookingDTO,
  type ExchangeOffersRequestPassengerSpecificationDTO,
  type PassengerDTO,
  type SearchOffersDTO,
} from '@/types/dto/agent';
import { buildSearchFormPassengers } from '@/utils/changeTrips';
import { formatFullDate } from '@/utils/dateTime';
import { showErrorToast } from '@/utils/errorToast';
import { parseJourneyChangeUrlParams } from '@/utils/journeyChangeUrlParams';
import type { SearchFormValues } from '@packages/webshop-search-widget';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// The link carries only the passenger's externalReference, but the offer request
// needs their type/age. No match means the exchange can't be scoped — callers
// treat that as an unusable link rather than widening to every passenger, which
// the BE rejects as a passenger/offer mismatch.
export const resolveMatchedPassengers = (
  booking: BookingDTO | null | undefined,
  passengerExternalRef: string | undefined
): Array<PassengerDTO> => {
  if (!passengerExternalRef) return [];
  return (booking?.passengers ?? []).filter(
    (p) => p.externalReference === passengerExternalRef
  );
};

const resolvePassengerSpecifications = (
  passengers: ReadonlyArray<PassengerDTO>
): Array<ExchangeOffersRequestPassengerSpecificationDTO> =>
  passengers.flatMap(({ externalReference, type, age }) =>
    externalReference && type
      ? [{ externalReference, type, ...(age !== undefined ? { age } : {}) }]
      : []
  );

// Locates the booked trip carrying the fulfillment being exchanged, so its
// admissions can seed the widget's locked passenger picker with the right types.
export const findTripByFulfillment = (
  booking: BookingDTO | null | undefined,
  fulfillmentId: string
): BookedTripDTO | undefined =>
  (booking?.bookedTrips ?? []).find((trip) =>
    (trip.bookedOffers ?? []).some((offer) =>
      (offer.admissions ?? []).some((admission) =>
        (admission.fulfillments ?? []).some((f) => f.id === fulfillmentId)
      )
    )
  );

type SearchStop = { code?: string; name?: string; cityName?: string };

// Converts a stop from the search results (or the booked trip) into the option
// shape the widget's origin/destination autocomplete expects.
const toSearchOption = (
  stop: SearchStop | null | undefined,
  fallbackCode: string | undefined
): SearchFormValues['origin'] | undefined => {
  const code = stop?.code ?? fallbackCode;
  if (!code) return undefined;
  const label = stop?.name ?? code;
  return {
    value: code,
    type: 'stop',
    label,
    cityName: stop?.cityName || label,
  };
};

// Stop names come from the first returned journey, falling back to the booked
// trip and finally the raw URL codes, which have no human-readable label.
const buildInitialFormValues = ({
  offers,
  bookedTrip,
  matchedPassengers,
  passengerTypes,
  departureTime,
  originStopCode,
  destinationStopCode,
}: {
  offers: SearchOffersDTO;
  bookedTrip: BookedTripDTO | undefined;
  matchedPassengers: ReadonlyArray<PassengerDTO>;
  passengerTypes: Parameters<typeof buildSearchFormPassengers>[2];
  departureTime: string;
  originStopCode: string | undefined;
  destinationStopCode: string | undefined;
}): SearchFormValues => {
  const journey = offers.journeys?.[0];
  const origin = toSearchOption(
    journey?.originStop ?? bookedTrip?.originStop,
    originStopCode
  );
  const destination = toSearchOption(
    journey?.destinationStop ?? bookedTrip?.destinationStop,
    destinationStopCode
  );

  return {
    productType: 'ticket',
    origin: origin ?? null,
    destination: destination ?? null,
    departureDate: new Date(departureTime),
    passengers: buildSearchFormPassengers(
      matchedPassengers,
      bookedTrip,
      passengerTypes
    ),
    promoCodes: [],
  };
};

// Drives the journey-change deep-link flow from the URL params. On mount it
// fetches the booking (for the passenger's type/age) and runs the token-scoped
// exchange-offers search; `search()` re-runs it for another route/date. The
// passenger set and token stay fixed, and a re-search failure only toasts,
// leaving the previous results in place.
export const useJourneyChangeDeepLink = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useMemo(() => parseJourneyChangeUrlParams(), []);
  const salesPointConfiguration = useSelector(salesPointConfigurationSelector);
  const currencyCode = salesPointConfiguration?.currencies?.[0];
  const passengerTypes = salesPointConfiguration?.passengerTypes;

  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchOffers, setSearchOffers] = useState<SearchOffersDTO>();
  const [searchFormValues, setSearchFormValues] = useState<SearchFormValues>();

  // Passenger specifications resolved once from the fetched booking; re-searches
  // reuse them (the passenger set is fixed by the token) instead of refetching.
  const passengerSpecsRef = useRef<
    Array<ExchangeOffersRequestPassengerSpecificationDTO>
  >([]);

  const failLink = useCallback(
    (error?: ApiErrorResponse) => {
      const message = <TransText i18nKey="journeyChangeFailed" />;
      if (error) {
        showErrorToast(message, error);
      } else {
        toast.error(message);
      }
      navigate('/purchase', { replace: true });
    },
    [navigate]
  );

  const runSearch = useCallback(
    async (
      overrides: {
        departureTime: string;
        originStopCode?: string;
        destinationStopCode?: string;
      },
      {
        isInitial,
        formValues,
      }: { isInitial: boolean; formValues?: SearchFormValues }
    ) => {
      const { booking, fulfillment, passengerExternalRef, exchangeToken } =
        params;

      // `currencyCode` comes from the sales-point configuration loaded at app
      // bootstrap, so it can still be missing on a cold deep-link open.
      if (
        !booking ||
        !fulfillment ||
        !exchangeToken ||
        !currencyCode ||
        !overrides.departureTime
      ) {
        if (isInitial) failLink();
        return;
      }

      if (!isInitial) {
        if (formValues) setSearchFormValues(formValues);
        setIsSearching(true);
      }

      try {
        // Only the initial search fetches the booking, to resolve the
        // passenger's type/age; re-searches reuse the cached specs.
        let fetchedBooking: BookingDTO | undefined;
        let matchedPassengers: ReadonlyArray<PassengerDTO> = [];
        if (isInitial) {
          fetchedBooking = await fetchBooking(booking);
          matchedPassengers = resolveMatchedPassengers(
            fetchedBooking,
            passengerExternalRef
          );
          passengerSpecsRef.current =
            resolvePassengerSpecifications(matchedPassengers);
          if (passengerSpecsRef.current.length === 0) {
            failLink();
            return;
          }
        }

        const offers = await findExchangeOffersByToken({
          booking,
          fulfillmentId: fulfillment,
          departureTime: overrides.departureTime,
          originStopCode: overrides.originStopCode,
          destinationStopCode: overrides.destinationStopCode,
          currency: currencyCode,
          passengerSpecifications: passengerSpecsRef.current,
          exchangeToken,
        });
        setSearchOffers(offers);

        if (isInitial) {
          setSearchFormValues(
            buildInitialFormValues({
              offers,
              bookedTrip: findTripByFulfillment(fetchedBooking, fulfillment),
              matchedPassengers,
              passengerTypes,
              departureTime: overrides.departureTime,
              originStopCode: overrides.originStopCode,
              destinationStopCode: overrides.destinationStopCode,
            })
          );
          setIsLoading(false);
        }
      } catch (e) {
        if (isInitial) {
          failLink(e as ApiErrorResponse);
        } else {
          toast.error(<TransAlert i18nKey="somethingWentWrong" />);
        }
      } finally {
        if (!isInitial) setIsSearching(false);
      }
    },
    [currencyCode, failLink, params, passengerTypes]
  );

  // The passenger set is fixed by the token, so form passengers are ignored.
  const search = useCallback(
    (values: SearchFormValues) => {
      runSearch(
        {
          departureTime: formatFullDate(values.departureDate),
          originStopCode: values.origin?.value,
          destinationStopCode: values.destination?.value,
        },
        { isInitial: false, formValues: values }
      );
    },
    [runSearch]
  );

  // Guards against StrictMode double-invoke firing a second search for the link.
  const initiated = useRef(false);

  useEffect(() => {
    if (initiated.current) return;
    initiated.current = true;

    // The flow writes into the purchase slice and the manage change-trips
    // selection without owning them, so a same-tab navigation from a prior
    // purchase/exchange could leave stale state the reused offer list and
    // checkout would render against. No-op in a fresh email-link tab.
    dispatch(resetPurchase());
    dispatch(resetChangeTripsSelection());

    const { originStopPlaceRef, destinationStopPlaceRef, departureTime } =
      params;
    const linkDeparture = departureTime ? new Date(departureTime) : undefined;
    runSearch(
      {
        departureTime:
          linkDeparture && !isNaN(linkDeparture.getTime())
            ? formatFullDate(linkDeparture)
            : '',
        originStopCode: originStopPlaceRef,
        destinationStopCode: destinationStopPlaceRef,
      },
      { isInitial: true }
    );
  }, [dispatch, params, runSearch]);

  return {
    isLoading,
    isSearching,
    searchOffers,
    searchFormValues,
    search,
  };
};
